import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, MessageSquareText } from 'lucide-react';
import { cn } from '@repo/ui/src/lib/utils';
import type { SessionNotesPayload } from '@repo/shared-types';

/** The 60px row between the attendance progress bar and the "Học viên"
 *  heading, shown only when the session is COMPLETED (handoff §1). */
export function SessionNotesEntryRow({
  notes,
  onOpen,
  isPending,
}: {
  notes: SessionNotesPayload | undefined;
  onOpen: () => void;
  /** The lazy notes query (gated on scroll-into-view, see TodaySessionCard) is
   *  still in flight — `notes` is `undefined` for that reason, not because
   *  there are no notes yet. Render a placeholder instead of the empty
   *  state, or a session that HAS notes would flash as empty on every mount. */
  isPending?: boolean;
}) {
  const { t } = useTranslation('progress');
  if (isPending) {
    return <div className="mt-5 h-[60px] w-full animate-pulse rounded-lg border border-surface-200 bg-surface-100" />;
  }
  // THI-193: sectioned items count as content, same as the free text.
  const hasClass = Boolean(notes?.classNote?.comment || notes?.classNote?.items.length);
  const hasHomework = Boolean(notes?.classNote?.homework || notes?.classNote?.homeworkItems.length);
  const studentCount = notes?.notedStudentIds.length ?? 0;
  const filled = hasClass || hasHomework || studentCount > 0;

  const summary = [
    hasClass ? t('notes.entry.summaryClass') : null,
    studentCount > 0 ? t('notes.entry.summaryStudents', { count: studentCount }) : null,
    hasHomework ? t('notes.entry.summaryHomework') : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'mt-5 flex min-h-[60px] w-full items-center gap-4 rounded-lg px-4 py-3 text-left',
        filled ? 'border border-surface-200 bg-surface-0' : 'border border-dashed border-brand-300 bg-surface-50',
      )}
    >
      <span
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-brand-700',
          filled ? 'bg-brand-100' : 'bg-brand-50',
        )}
      >
        {filled ? (
          <Check className="h-[17.6px] w-[17.6px]" strokeWidth={2.4} />
        ) : (
          <MessageSquareText className="h-[17.6px] w-[17.6px]" strokeWidth={1.8} />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14.5px] font-semibold text-surface-800">{t('notes.entry.title')}</span>
        <span className="mt-0.5 block truncate text-[12.5px] text-surface-500">
          {filled ? summary : t('notes.entry.empty')}
        </span>
      </span>
      {filled ? (
        <span className="shrink-0 text-[12.5px] font-medium text-brand-600">{t('notes.entry.edit')}</span>
      ) : (
        <ChevronRight className="h-[18px] w-[18px] shrink-0 text-surface-400" strokeWidth={2} />
      )}
    </button>
  );
}
